import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  ShieldCheck,
  CreditCard,
  RefreshCcw,
  Lock,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Scale,
  PhoneCall,
} from "lucide-react";
import { Link } from "react-router-dom";

const COMPANY_NAME = "SwitchIn";
const PRODUCT_NAME = "SwitchIn Jewellery Retail Software";
const WEBSITE_NAME = "SwitchIn";
const SUPPORT_EMAIL = "nextloopinfotech@gmail.com";
const SUPPORT_PHONE = "+91 96996 24618";
const LEGAL_JURISDICTION = "Pune, Maharashtra, India";

// yaha bas ye change karna hai:
// "7 days" ya "30 days"
const TRIAL_PERIOD = "7 days";

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
});

const termsSections = [
  {
    id: "acceptance",
    icon: <FileText className="h-5 w-5" />,
    title: "1. Acceptance of Terms",
    content: [
      `These Terms of Service ("Terms") govern your access to and use of ${PRODUCT_NAME}, including related web pages, software features, support services, onboarding, training, and any other associated services provided by ${COMPANY_NAME}.`,
      `By accessing, registering for, using, or continuing to use ${WEBSITE_NAME}, you agree to be bound by these Terms. If you do not agree with these Terms, you must not use the service.`,
    ],
  },
  {
    id: "eligibility",
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "2. Eligibility and Authorised Use",
    content: [
      `The service is intended for jewellery retailers, store owners, managers, authorised staff, and business representatives who are legally capable of entering into binding agreements.`,
      `You agree that you will use the software only for lawful business purposes and that you are authorised to act on behalf of your store, business, or organisation where applicable.`,
    ],
  },
  {
    id: "account",
    icon: <Lock className="h-5 w-5" />,
    title: "3. Account Registration and Responsibility",
    content: [
      "You may be required to provide accurate information such as store name, contact details, branch information, and user details to access the software.",
      "You are responsible for maintaining the confidentiality of your login credentials and for all activity carried out using your account.",
      "You agree to notify us immediately if you suspect any unauthorised access, misuse, or security issue related to your account.",
    ],
  },
  {
    id: "trial",
    icon: <CheckCircle2 className="h-5 w-5" />,
    title: "4. Free Trial",
    content: [
      `We may offer a free trial of ${TRIAL_PERIOD} to eligible users, stores, or businesses, unless otherwise communicated by us in writing.`,
      "The free trial is provided only for evaluation purposes and may include limited access, limited modules, limited support, or limited features at our discretion.",
      "After the free trial period ends, continued use of the software requires payment of the applicable subscription or service fee.",
      "We reserve the right to modify, withdraw, restrict, or discontinue the free trial offer at any time.",
    ],
  },
  {
    id: "pricing",
    icon: <CreditCard className="h-5 w-5" />,
    title: "5. Subscription, Pricing and Payment",
    content: [
      "Paid use of the software is subject to the pricing plan, billing cycle, and commercial terms agreed between you and us at the time of subscription.",
      "Fees may be charged on a monthly, yearly, or custom commercial basis depending on the plan selected for your business.",
      "You agree to make payment on time and understand that failure to pay applicable charges may result in suspension, limitation, or termination of access to the software.",
      "Unless explicitly stated otherwise in writing, all prices are exclusive of taxes, duties, levies, or statutory charges that may apply.",
    ],
  },
  {
    id: "refund",
    icon: <RefreshCcw className="h-5 w-5" />,
    title: "6. Refund Policy",
    content: [
      "Once payment has been made for any subscription, onboarding, setup, implementation, training, or related service, such payment shall be treated as final.",
      "No refund shall be provided after payment is successfully received, except where required by applicable law or where we expressly approve a refund in writing.",
      "You are encouraged to use the free trial, ask questions, and evaluate the software before making payment.",
    ],
    highlight: true,
  },
  {
    id: "use",
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "7. Acceptable Use",
    content: [
      "You agree not to misuse the software or use it in any unlawful, fraudulent, harmful, abusive, or unauthorised manner.",
      "You must not attempt to copy, reverse engineer, hack, disrupt, overload, disable, scrape, or interfere with the software, servers, security layers, or connected services.",
      "You must not use the software to store or transmit unlawful data, malicious code, viruses, or material that infringes the rights of others.",
    ],
  },
  {
    id: "data",
    icon: <Lock className="h-5 w-5" />,
    title: "8. Data, Privacy and Security",
    content: [
      "You retain responsibility for the business data, customer data, billing information, stock records, and other data entered into the software by you or your staff.",
      "We may process and store such data only for the purpose of providing, maintaining, securing, supporting, improving, or legally complying in relation to the service.",
      `Your use of the software is also subject to our Privacy Policy. In case of any conflict between operational use and privacy handling, the Privacy Policy shall apply to personal data matters.`,
    ],
  },
  {
    id: "availability",
    icon: <AlertTriangle className="h-5 w-5" />,
    title: "9. Service Availability and Support",
    content: [
      "We aim to keep the software available and functioning smoothly, but we do not guarantee uninterrupted, error-free, or always-available operation.",
      "The service may occasionally be unavailable due to maintenance, upgrades, internet issues, third-party downtime, technical faults, or events beyond our control.",
      "Support, onboarding, and training may be provided according to the plan or commercial agreement applicable to your account.",
    ],
  },
  {
    id: "ip",
    icon: <FileText className="h-5 w-5" />,
    title: "10. Intellectual Property",
    content: [
      `${PRODUCT_NAME}, including its design, code, workflows, features, branding, layout, content, documentation, and all associated intellectual property, remains the exclusive property of ${COMPANY_NAME} and/or its licensors.`,
      "You are granted a limited, non-exclusive, non-transferable, revocable right to use the software for your internal business operations during the active subscription period.",
      "You may not reproduce, resell, distribute, modify, sublicense, or commercially exploit the software without prior written permission.",
    ],
  },
  {
    id: "termination",
    icon: <AlertTriangle className="h-5 w-5" />,
    title: "11. Suspension and Termination",
    content: [
      "We may suspend, restrict, or terminate access to the software if you violate these Terms, fail to pay charges, misuse the platform, create security risks, or act in a way that harms the service or other users.",
      "You may stop using the software at any time. However, stopping use does not automatically entitle you to any refund.",
      "We may also terminate or modify any feature, plan, or offering if required for technical, legal, operational, or business reasons.",
    ],
  },
  {
    id: "liability",
    icon: <Scale className="h-5 w-5" />,
    title: "12. Limitation of Liability",
    content: [
      "To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, including loss of profits, loss of business, loss of goodwill, loss of data, or interruption of operations.",
      "You acknowledge that software usage involves operational and internet-based risks, and you remain responsible for verifying important business outputs such as billing, tax, pricing, and inventory data.",
      "Our total liability arising out of or related to the service shall not exceed the amount actually paid by you for the relevant service period giving rise to the claim.",
    ],
  },
  {
    id: "law",
    icon: <Scale className="h-5 w-5" />,
    title: "13. Governing Law and Jurisdiction",
    content: [
      `These Terms shall be governed by and interpreted in accordance with the laws of India.`,
      `Any disputes arising out of or relating to these Terms or the service shall be subject to the exclusive jurisdiction of the courts located in ${LEGAL_JURISDICTION}.`,
    ],
  },
  {
    id: "changes",
    icon: <FileText className="h-5 w-5" />,
    title: "14. Changes to These Terms",
    content: [
      "We may update or modify these Terms from time to time to reflect legal, operational, product, pricing, support, or business changes.",
      "Updated Terms will become effective when published on the website or otherwise communicated to you.",
      "Continued use of the software after such update means you accept the revised Terms.",
    ],
  },
  {
    id: "contact",
    icon: <PhoneCall className="h-5 w-5" />,
    title: "15. Contact Us",
    content: [
      `If you have any questions about these Terms, billing, trial access, subscription, or support, you may contact us at ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`,
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4ff] via-[#fdf9f3] to-[#e8f4fc] text-slate-900">
      {/* background */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(129,140,248,0.18),transparent),radial-gradient(ellipse_60%_40%_at_100%_100%,rgba(245,158,11,0.12),transparent),radial-gradient(ellipse_40%_30%_at_0%_100%,rgba(236,72,153,0.08),transparent)]" />
        <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-[#6366f1]/20 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-[#f97316]/15 blur-3xl" />
      </div>

      <main className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-12">
        {/* top bar */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp(0)}
          className="mb-6"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-violet-300 hover:text-violet-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </motion.div>

        {/* hero */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.05)}
          className="overflow-hidden rounded-[30px] border border-violet-200/70 bg-white/90 p-6 shadow-xl shadow-violet-200/30 backdrop-blur-sm md:p-8"
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-700 ring-1 ring-violet-200/70">
                <FileText className="h-3.5 w-3.5" />
                Legal
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                Terms of Service
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-[15px]">
                These Terms explain the rules for using {PRODUCT_NAME},
                including trial access, paid subscription, non-refundable
                payments, account use, and service conditions.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
                  Free trial available
                </span>
                <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200/70">
                  Payment required after trial
                </span>
                <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200/70">
                  No refund after payment
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-600 shadow-sm">
              <p className="font-semibold text-slate-800">Document details</p>
              <p className="mt-2">
                <span className="font-medium text-slate-700">Product:</span>{" "}
                {PRODUCT_NAME}
              </p>
              {/* <p className="mt-1">
                <span className="font-medium text-slate-700">
                  Last updated:
                </span>{" "}
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </p> */}
              <p className="mt-1">
                <span className="font-medium text-slate-700">Trial:</span>{" "}
                {TRIAL_PERIOD}
              </p>
            </div>
          </div>
        </motion.section>

        {/* important summary */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.1)}
          className="mt-6 rounded-[28px] border border-amber-200/80 bg-gradient-to-r from-amber-50 via-white to-rose-50 p-5 shadow-sm"
        >
          <h2 className="text-base font-semibold text-slate-900">
            Important summary
          </h2>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Free Trial
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-slate-700">
                A free trial of {TRIAL_PERIOD} may be offered for evaluation.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Payment After Trial
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-slate-700">
                After the trial ends, payment is required to continue using the
                software.
              </p>
            </div>

            <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">
                Refund Policy
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-rose-700">
                Once payment is received, no refund will be provided.
              </p>
            </div>
          </div>
        </motion.section>

        {/* sections */}
        <section className="mt-8 grid gap-4">
          {termsSections.map((section, index) => (
            <motion.article
              key={section.id}
              initial="hidden"
              animate="visible"
              variants={fadeUp(0.05 + index * 0.02)}
              className={`rounded-[26px] border bg-white/95 p-5 shadow-sm backdrop-blur-sm md:p-6 ${
                section.highlight
                  ? "border-rose-200 ring-1 ring-rose-100"
                  : "border-slate-200/80"
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                    section.highlight
                      ? "bg-rose-100 text-rose-700"
                      : "bg-violet-100 text-violet-700"
                  }`}
                >
                  {section.icon}
                </div>

                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {section.title}
                  </h2>

                  <div className="mt-3 space-y-3">
                    {section.content.map((paragraph, idx) => (
                      <p
                        key={idx}
                        className="text-sm leading-7 text-slate-600 md:text-[15px]"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </section>

        {/* footer block */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.2)}
          className="mt-8 rounded-[28px] border border-slate-200/80 bg-white/90 p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-slate-900">
            Need help with these terms?
          </h3>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            For questions related to trial, payment, subscription, support, or
            legal policies, contact our team.
          </p>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black"
            >
              Email support
            </a>

            <a
              href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition hover:border-violet-300 hover:text-violet-700"
            >
              Call support
            </a>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
